import { Grid, Card } from '@mui/material';

import { CompanyDetailsResponse } from '@/models/payment';
import { ButtonContainer } from '@/pages/company/PaymentSetting';
import { SubTitle, Title } from '@/typography/Typography';
import {
    SecondaryButtonRegular,
    PrimaryButton
} from '@/components/shared/Button';
import { InputText } from '@/components/shared/InputText';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useErrorHandling } from '@/utils/errorHandling';
import { GridContainer } from '@/pages/company/componants/Base';

export const WithdrawalLimitsSettings = ({
    currentCompany
}: {
    currentCompany?: CompanyDetailsResponse;
}) => {
    const { t } = useTranslation();

    const [withdrawalCountPerDay, setWithdrawalLimitForDay] =
        useState<number>();
    const [withdrawalMaxAmountPerLast7Days, setWithdrawalLimitFor7Days] =
        useState<number>();
    const [
        withdrawalMaxAmountPerTransaction,
        setWithdrawalLimitForPerTransaction
    ] = useState<number>();

    const initialValues = () => {
        setWithdrawalLimitForDay(currentCompany?.withdrawalCountPerDay);
        setWithdrawalLimitFor7Days(
            currentCompany?.withdrawalMaxAmountPerLast7Days
        );
        setWithdrawalLimitForPerTransaction(
            currentCompany?.withdrawalMaxAmountPerTransaction
        );
    };

    useEffect(() => {
        initialValues();
    }, [
        currentCompany?.withdrawalCountPerDay,
        currentCompany?.withdrawalMaxAmountPerLast7Days,
        currentCompany?.withdrawalMaxAmountPerTransaction
    ]);

    const { updateCompanyDetails } = usePaymentsMutations();

    const { setAlert } = useNotificationsSnackbar();

    useEffect(() => {
        if (updateCompanyDetails.isSuccess) {
            setAlert(t('limits_updated'), 'success');
        }
    }, [updateCompanyDetails.isSuccess]);

    const { backendErrorHandler } = useErrorHandling();

    useEffect(() => {
        if (updateCompanyDetails.isError) {
            backendErrorHandler()(updateCompanyDetails.error);
        }
    }, [updateCompanyDetails.isError]);

    return (
        <Grid
            container
            mt={3}
            pt={5}
            justifyContent={'space-between'}
            borderTop={'1px solid #D0D5DD'}
        >
            <Grid item xs={3} display={'block'}>
                <Title>{t('withdrawal_limits')}</Title>
                <SubTitle marginBottom={2}>
                    {t('withdrawal_limits_note')}
                </SubTitle>
            </Grid>
            <Grid item xs={8.8}>
                <Card>
                    <GridContainer container margin={'0 !important'}>
                        <InputText
                            type="number"
                            value={withdrawalCountPerDay}
                            onChange={(e) => {
                                setWithdrawalLimitForDay(e.target.value as any);
                            }}
                            label={t('withdrawal_limit_for_day')}
                        />
                        <SubTitle>
                            {t('withdrawal_limit_for_day_note')}
                        </SubTitle>
                    </GridContainer>

                    <GridContainer container margin={'0 !important'}>
                        <InputText
                            type="number"
                            value={withdrawalMaxAmountPerLast7Days}
                            onChange={(e) => {
                                setWithdrawalLimitFor7Days(
                                    e.target.value as any
                                );
                            }}
                            label={t('withdrawal_limit_for_7_days', {
                                currency: currentCompany?.currency
                            })}
                        />
                        <SubTitle>
                            {t('withdrawal_limit_for_7_days_note')}
                        </SubTitle>
                    </GridContainer>

                    <GridContainer container margin={'0 !important'}>
                        <InputText
                            type="number"
                            value={withdrawalMaxAmountPerTransaction}
                            onChange={(e) => {
                                setWithdrawalLimitForPerTransaction(
                                    e.target.value as any
                                );
                            }}
                            label={t('withdrawal_limit_for_per_transaction', {
                                currency: currentCompany?.currency
                            })}
                        />
                        <SubTitle>
                            {t('withdrawal_limit_for_per_transaction_note')}
                        </SubTitle>
                    </GridContainer>

                    <ButtonContainer mt={3}>
                        <SecondaryButtonRegular onClick={initialValues}>
                            {t('cancel')}
                        </SecondaryButtonRegular>
                        <PrimaryButton
                            sx={{ marginLeft: '6px !important' }}
                            size="medium"
                            loading={updateCompanyDetails.isPending}
                            // disabled={updateCompanyDetails.isPending}
                            onClick={() => {
                                updateCompanyDetails.mutate({
                                    withdrawalCountPerDay,
                                    withdrawalMaxAmountPerLast7Days,
                                    withdrawalMaxAmountPerTransaction
                                });
                            }}
                        >
                            {t('save_changes')}
                        </PrimaryButton>
                    </ButtonContainer>
                </Card>
            </Grid>
        </Grid>
    );
};
